import {
  type ColumnDef,
  type OnChangeFn,
  type PaginationOptions,
  type PaginationState,
  type RowSelectionState,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import clsx from "clsx";
import type { PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";

// import { Pagination } from "@/components/ui/table/Pagination";
import { TextButton } from "@/components/ui/buttons/TextButton/TextButton";
import { Loader } from "@/components/ui/status/Loader/Loader";
import { Pagination } from "@/components/ui/table/Pagination";
import { Typography } from "@/components/ui/text/Typography/Typography";

export type PaginationParams = PaginationState;
export type SortParams = { sortBy: `${string}.${"asc" | "desc"}` };

export type Filters<T> = Partial<T & PaginationParams & SortParams>;

export interface SeeAllData {
  totalItemsCount: number;
  onSeeAll: () => void;
  buttonText?: string;
}

export const tableClasses = {
  table: "min-h-[90px] w-full max-w-[1300px] overflow-x-auto rounded-xs border border-elevation-outline-default-1",
  tableHeader: "border-b border-elevation-outline-default-1 bg-elevation-fill-default-2",
  tableRow:
    "group border-b border-elevation-outline-default-1  transition-colors duration-150 hover:bg-elevation-fill-default-2",
  tableCell: "min-w-10 py-0",
  tableNoData: "p-3 text-center",
  tableLoading: "flex w-full items-center justify-center",
};

interface TableProps<TData> extends PropsWithChildren {
  data: TData[];
  columns: ColumnDef<TData>[];
  pagination?: PaginationState;
  paginationOptions?: Pick<PaginationOptions, "onPaginationChange" | "rowCount" | "pageCount">;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  onRowClick?: (row: TData) => void;
  onPaginationChange?: OnChangeFn<PaginationState>;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  rowSelection?: RowSelectionState;
  isLoading?: boolean;
  className?: string;
  rowClassname?: (row: TData) => string;
  manualSorting?: boolean;
  seeAllData?: SeeAllData;
  tableLayout?: "fixed" | "auto";
}

export const Table = <TData,>({
  data,
  columns,
  pagination,
  paginationOptions,
  sorting,
  onSortingChange,
  children,
  manualSorting = true,
  onRowClick,
  onPaginationChange,
  onRowSelectionChange,
  isLoading,
  rowSelection,
  className,
  rowClassname,
  seeAllData,
  tableLayout = "fixed",
}: TableProps<TData>) => {
  const { t } = useTranslation();
  const table = useReactTable({
    data,
    columns,
    state: { pagination, sorting, rowSelection },
    onSortingChange,
    ...paginationOptions,
    manualFiltering: true,
    manualSorting,
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    enableRowSelection: true,
    getCoreRowModel: getCoreRowModel(),
    onRowSelectionChange,
    onPaginationChange,
    defaultColumn: {
      size: undefined,
    },
  });

  return (
    <div className="flex w-full flex-1 flex-col items-center gap-3">
      {/* Filters */}
      {children}
      {/* Table */}
      <div className={clsx(tableClasses.table, className)}>
        <table
          className="w-full px-3"
          style={{ tableLayout }}
        >
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr
                key={headerGroup.id}
                className={tableClasses.tableHeader}
              >
                {headerGroup.headers.map((header) => {
                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      style={{
                        width: header?.column.columnDef.size ? header.column.getSize() : undefined,
                        maxWidth: header?.column.columnDef.size ? header.column.getSize() : undefined,
                      }}
                      className={tableClasses.tableCell}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {!isLoading &&
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className={clsx(rowClassname?.(row.original), tableClasses.tableRow, onRowClick && "cursor-pointer")}
                  onClick={() => onRowClick?.(row.original)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      style={{
                        width: cell?.column.columnDef.size ? cell.column.getSize() : undefined,
                      }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            {!isLoading && seeAllData && table.getRowModel().rows.length > 0 && (
              <tr className={tableClasses.tableRow}>
                <td
                  colSpan={columns.length}
                  className={tableClasses.tableCell}
                >
                  <TextButton
                    onPress={seeAllData.onSeeAll}
                    className="w-auto p-3! text-left"
                    // variant="default"
                    // size="label-4"
                  >
                    {seeAllData.buttonText || t("table.seeAll", { count: seeAllData.totalItemsCount })}
                  </TextButton>
                </td>
              </tr>
            )}
            {!isLoading && table.getRowModel().rows.length === 0 && (
              <tr>
                <td
                  colSpan={columns.length}
                  className={tableClasses.tableNoData}
                >
                  <Typography
                    size="body-paragraph-m"
                    variant="default"
                    className="text-white"
                  >
                    {t("table.noData")}
                  </Typography>
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {isLoading && (
          <div className={tableClasses.tableLoading}>
            <Loader size="l" />
          </div>
        )}
      </div>

      {pagination && (
        <Pagination
          pagination={pagination}
          table={table}
        />
      )}
    </div>
  );
};
